import { useState, useRef, useEffect } from "react";
import {
  Upload,
  Search,
  Grid3X3,
  List,
  ImageIcon,
  FileText,
  Video,
  File,
  Copy,
  Trash2,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  media_uploads_view,
  get_media_library,
  APL_LINK,
} from "../serviceconnection/ServiceConnection";

// ─── Constants ────────────────────────────────────────────────────────────────
const typeIcon = {
  image: ImageIcon,
  video: Video,
  document: FileText,
  file: File,
};
const typeColor = {
  image: "hsl(var(--accent))",
  video: "hsl(280 55% 55%)",
  document: "hsl(var(--info))",
  file: "hsl(var(--muted-foreground))",
};

// ─── Helper: construct public URL from generated_name ─────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
export default function MediaLibrary() {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [imageLinkPrefix, setImageLinkPrefix] = useState("");
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("grid");
  const [typeFilter, setTypeFilter] = useState("All");
  const [dragging, setDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);

  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchMedia = async () => {
    try {
      setListLoading(true);
      const fd = new FormData();
      fd.append("main_id", 0);
      const res = await server_post_data(get_media_library, fd);
      setMediaFiles(res.data?.data ?? []);
      console.log("Media files:", res.data);
      setImageLinkPrefix(APL_LINK + (res.data?.path_image || ""));
    } catch {
      showError("Failed to load media library");
    } finally {
      setListLoading(false);
    }
  };

  const getFileUrl = (file) =>
    file.file_type === "image" ? imageLinkPrefix + file.generated_name : null;

  useEffect(() => {
    fetchMedia();
  }, []);

  // ── Derived stats ─────────────────────────────────────────────────────────
  const imageCount = mediaFiles.filter((f) => f.file_type === "image").length;
  const videoCount = mediaFiles.filter((f) => f.file_type === "video").length;
  const documentCount = mediaFiles.filter(
    (f) => f.file_type === "document",
  ).length;

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = mediaFiles.filter(
    (f) =>
      f.original_name?.toLowerCase().includes(search.toLowerCase()) &&
      (typeFilter === "All" || f.file_type === typeFilter),
  );

  // ── File picker ───────────────────────────────────────────────────────────
  const openFilePicker = () => fileInputRef.current?.click();

  const addPendingFiles = (files) => {
    const entries = files.map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      previewUrl: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null,
      type: file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("video/")
          ? "video"
          : "document",
    }));
    setPendingFiles((prev) => [...prev, ...entries]);
  };

  const removePendingFile = (id) => {
    setPendingFiles((prev) => {
      const entry = prev.find((f) => f.id === id);
      if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  };

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) addPendingFiles(files);
    e.target.value = "";
  };

  // ── Upload to backend ─────────────────────────────────────────────────────
  const handleUploadToBackend = async () => {
    if (!pendingFiles.length) {
      showError("Please select files first");
      return;
    }
    try {
      setUploadLoading(true);
      const fd = new FormData();
      // Pass admin_id if available from localStorage / auth context
      // fd.append("admin_id", adminId);
      pendingFiles.forEach((item) => fd.append("files", item.file));

      const res = await server_post_data(media_uploads_view, fd);
      if (res.data?.error) {
        showError(res.data?.message || "Upload failed");
        return;
      }

      showSuccess(res.data?.message ?? "Files uploaded successfully");

      // Cleanup previews
      pendingFiles.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
      setPendingFiles([]);

      // Refresh list
      fetchMedia();
    } catch {
      showError("Something went wrong");
    } finally {
      setUploadLoading(false);
    }
  };

  // ── Drag & Drop ───────────────────────────────────────────────────────────
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.items?.length > 0) setDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setDragging(false);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    dragCounter.current = 0;
    const files = Array.from(e.dataTransfer.files);
    if (files.length) addPendingFiles(files);
    e.dataTransfer.clearData();
  };

  // ── Copy URL ──────────────────────────────────────────────────────────────
  const copyUrl = (file) => {
    const url = imageLinkPrefix + file.generated_name;
    navigator.clipboard.writeText(url).then(() => showSuccess("URL copied!"));
  };

  const getFileThumbnail = (file) => {
    const name = (file.original_name ?? file.file?.name ?? "").toLowerCase();

    // video by file_type or extension
    if (
      file.file_type === "video" ||
      file.type === "video" ||
      name.endsWith(".mp4") ||
      name.endsWith(".mov") ||
      name.endsWith(".webm") ||
      name.endsWith(".ogg")
    )
      return "/file-icons/thumb-video.png";

    // pdf by extension
    if (name.endsWith(".pdf")) return "/file-icons/thumb-pdf.webp";

    // excel/csv by extension
    if (
      name.endsWith(".xls") ||
      name.endsWith(".xlsx") ||
      name.endsWith(".csv")
    )
      return "/file-icons/thumb-excel.jpg";

    // word/doc by extension
    if (name.endsWith(".doc") || name.endsWith(".docx"))
      return "/file-icons/thumb-file.webp";

    return null;
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[1400px] mx-auto space-y-5">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.svg"
        className="hidden"
        onChange={handleFilesSelected}
      />

      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Media Library</h1>
          <p className="page-subtitle">
            {mediaFiles.length} files &middot; Images, videos, documents
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchMedia}
            className="w-9 h-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:bg-muted"
            title="Refresh"
          >
            <RefreshCw
              className={`w-4 h-4 ${listLoading ? "animate-spin" : ""}`}
            />
          </button>
          <button
            onClick={openFilePicker}
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white"
            style={{ background: "hsl(var(--accent))" }}
          >
            <Upload className="w-4 h-4" /> Upload Files
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Images", value: imageCount, color: "hsl(var(--accent))" },
          { label: "Videos", value: videoCount, color: "hsl(280 55% 55%)" },
          {
            label: "Documents",
            value: documentCount,
            color: "hsl(var(--info))",
          },
          {
            label: "Total Files",
            value: mediaFiles.length,
            color: "hsl(var(--warning))",
          },
        ].map((s) => (
          <div key={s.label} className="cms-card p-4">
            <p className="text-2xl font-bold" style={{ color: s.color }}>
              {s.value}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Drop Zone ── */}
      <div
        className="border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer"
        style={{
          borderColor: dragging ? "hsl(var(--accent))" : "hsl(var(--border))",
          background: dragging
            ? "hsl(var(--accent) / 0.06)"
            : "hsl(var(--muted) / 0.8)",
        }}
        onClick={openFilePicker}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3 transition-transform"
          style={{
            background: "hsl(var(--accent-muted))",
            transform: dragging ? "scale(1.1)" : "scale(1)",
          }}
        >
          <Upload className="w-6 h-6" style={{ color: "hsl(var(--accent))" }} />
        </div>
        <p className="text-sm font-semibold text-foreground">
          {dragging ? "Release to upload" : "Drop files here to upload"}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          or{" "}
          <span
            className="font-medium cursor-pointer hover:underline"
            style={{ color: "hsl(var(--accent))" }}
          >
            browse your computer
          </span>
        </p>
        <p className="text-[11px] text-muted-foreground mt-2">
          Supports: JPG, PNG, WebP, SVG, PDF, MP4, DOCX, XLSX · Max 50MB
        </p>
      </div>

      {/* ── Pending uploads ── */}
      {pendingFiles.length > 0 && (
        <div className="section-panel">
          <div className="px-4 pt-4 pb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">
              Ready to Upload
              <span
                className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{
                  background: "hsl(var(--accent-muted))",
                  color: "hsl(var(--accent))",
                }}
              >
                {pendingFiles.length}
              </span>
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  pendingFiles.forEach((f) => {
                    if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
                  });
                  setPendingFiles([]);
                }}
                className="text-xs text-muted-foreground hover:text-destructive transition-colors"
              >
                Clear all
              </button>
              <button
                onClick={handleUploadToBackend}
                disabled={uploadLoading}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60"
                style={{ background: "hsl(var(--accent))" }}
              >
                {uploadLoading ? "Uploading..." : "Upload All"}
              </button>
            </div>
          </div>
          <div className="p-4 grid grid-cols-3 sm:grid-cols-5 xl:grid-cols-8 gap-3">
            {pendingFiles.map((entry) => {
              const Icon = typeIcon[entry.type] || File;
              return (
                <div key={entry.id} className="relative group">
                  <div
                    className="h-20 rounded-lg flex items-center justify-center overflow-hidden border border-border"
                    style={{ background: "hsl(var(--muted))" }}
                  >
                    {entry.previewUrl ? (
                      <img
                        src={entry.previewUrl}
                        alt={entry.file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      // <Icon
                      //   className="w-7 h-7"
                      //   style={{ color: typeColor[entry.type] }}
                      // />
                      <img
                        src={getFileThumbnail(entry)}
                        alt={entry.type}
                        className="w-full h-full object-contain p-2"
                      />
                    )}
                  </div>
                  <button
                    onClick={() => removePendingFile(entry.id)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-white shadow-md hover:scale-110"
                    style={{ background: "hsl(0 72% 51%)" }}
                  >
                    <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                      <path
                        d="M1 1l8 8M9 1L1 9"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  <p className="text-[10px] text-muted-foreground mt-1 truncate text-center px-0.5">
                    {entry.file.name}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Filters + View toggle ── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="search-input flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files..."
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        {["All", "image", "video", "document"].map((t) => (
          <button
            key={t}
            onClick={() => setTypeFilter(t)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors capitalize ${
              typeFilter === t
                ? "text-white border-transparent"
                : "border-border text-muted-foreground hover:border-accent/30"
            }`}
            style={typeFilter === t ? { background: "hsl(var(--accent))" } : {}}
          >
            {t}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1 border border-border rounded-lg overflow-hidden">
          {[
            { key: "grid", Icon: Grid3X3 },
            { key: "list", Icon: List },
          ].map(({ key, Icon }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={`px-3 py-2 transition-colors ${view === key ? "text-white" : "text-muted-foreground hover:bg-muted"}`}
              style={view === key ? { background: "hsl(var(--accent))" } : {}}
            >
              <Icon className="w-4 h-4" />
            </button>
          ))}
        </div>
      </div>

      {/* ── Loading State ── */}
      {listLoading ? (
        <div className="text-center py-16 text-muted-foreground text-sm">
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm">
          No files found
        </div>
      ) : view === "grid" ? (
        /* ── Grid View ── */
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((f) => {
            const Icon = typeIcon[f.file_type] || File;
            // const url = imageLinkPrefix + f.generated_name;
            const url =
              f.file_type === "image"
                ? imageLinkPrefix + f.generated_name
                : null;
            return (
              <div
                key={f.primary_id}
                className="cms-card p-0 overflow-hidden group cursor-pointer"
              >
                <div
                  className="h-32 flex items-center justify-center relative overflow-hidden"
                  style={{ background: "hsl(var(--muted))" }}
                >
                  {url ? (
                    <img
                      src={url}
                      alt={f.original_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    // <Icon
                    //   className="w-10 h-10"
                    //   style={{ color: typeColor[f.file_type] }}
                    // />
                    <img
                      src={getFileThumbnail(f)}
                      alt={f.file_type}
                      className="w-full h-full object-contain p-3"
                    />
                  )}
                  <div className="absolute inset-0 bg-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => copyUrl(f)}
                      className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
                      title="Copy URL"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      className="w-8 h-8 rounded-lg bg-red-500/40 flex items-center justify-center text-white hover:bg-red-500/60"
                      title="Delete (coming soon)"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-xs font-semibold text-foreground truncate">
                    {f.original_name}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {f.file_size} &middot;{" "}
                    {f.uploaded_at
                      ? new Date(f.uploaded_at).toLocaleDateString("en-IN")
                      : ""}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── List View ── */
        <div className="section-panel">
          <div className="overflow-x-auto">
            <table className="w-full cms-table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Uploaded At</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => {
                  const Icon = typeIcon[f.file_type] || File;
                  // const url = imageLinkPrefix + f.generated_name;
                  const url =
                    f.file_type === "image"
                      ? imageLinkPrefix + f.generated_name
                      : null;
                  return (
                    <tr key={f.primary_id} className="group">
                      <td>
                        <div className="flex items-center gap-2.5">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{
                              background: `${typeColor[f.file_type]}18`,
                            }}
                          >
                            {/* <Icon
                              className="w-4 h-4"
                              style={{ color: typeColor[f.file_type] }}
                            /> */}
                            <img
                              src={getFileThumbnail(f)}
                              alt={f.file_type}
                              className="w-4 h-4 object-contain"
                            />
                          </div>
                          <span className="font-medium text-foreground text-sm">
                            {f.original_name}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="capitalize text-muted-foreground">
                          {f.file_type}
                        </span>
                      </td>
                      <td className="text-muted-foreground">{f.file_size}</td>
                      <td className="text-muted-foreground">
                        {f.uploaded_at
                          ? new Date(f.uploaded_at).toLocaleDateString("en-IN")
                          : "—"}
                      </td>
                      <td>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => copyUrl(f)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                            title="Copy URL"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          {url && (
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          <button
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                            style={{ color: "hsl(var(--destructive))" }}
                            title="Delete (coming soon)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Showing {filtered.length} of {mediaFiles.length} files
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
